import { useApi, useLoad } from "@allergy123/api-client/react";
import { BILLING_PLANS, type BillingAccount, type BillingPlan, type UpsertBillingAccountRequest } from "@allergy123/contracts";
import { Button, Card, Field, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { BILLING_PLAN_LABELS, billingErrorText, isStale, todayCalendarDate } from "./billing/common";

type AccountStatus = BillingAccount["status"];

interface Draft {
  plan: BillingPlan;
  effectiveOn: string;
  termMonths: string;
  status: AccountStatus;
}

const STATUS_OPTIONS: { value: AccountStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "closed", label: "Closed" },
];

const draftOf = (a: BillingAccount | null | undefined): Draft => ({
  plan: a?.plan ?? "bas",
  effectiveOn: a?.effectiveOn ?? todayCalendarDate(),
  termMonths: String(a?.termMonths ?? 12),
  status: a?.status ?? "active",
});

/** Keyed by id, so a different organization never shows the last one's unsaved choices or messages. */
export function OrganizationBillingPage() {
  const { id = "" } = useParams();
  return <OrganizationBillingEditor key={id} id={id} />;
}

/**
 * The organization's billing account (ADR 0008): plan and term, the Stripe
 * customer, whether a payment method is on file, and the Program Fee
 * subscription. The contract itself is signed outside the platform; nothing
 * here opens Stripe in the browser, and no card detail ever reaches this page.
 */
function OrganizationBillingEditor({ id }: { id: string }) {
  const api = useApi();
  const billing = useLoad((a) => a.admin.billing.account(id));
  const [draft, setDraft] = useState<Draft | null>(null);
  const [customerId, setCustomerId] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [setupLink, setSetupLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const account = billing.data?.account ?? null;
  const customer = billing.data?.customer ?? null;
  const form = draft ?? draftOf(account);
  const saved = draftOf(account);
  const changed = form.plan !== saved.plan || form.effectiveOn !== saved.effectiveOn || form.termMonths !== saved.termMonths || form.status !== saved.status;
  const termMonths = Number(form.termMonths);
  const termValid = Number.isInteger(termMonths) && termMonths >= 1 && termMonths <= 120;

  // Once a reload shows the account with the choices that were saved, the draft is no longer needed.
  useEffect(() => {
    if (!draft || !billing.data) return;
    const s = draftOf(billing.data.account);
    if (draft.plan === s.plan && draft.effectiveOn === s.effectiveOn && draft.termMonths === s.termMonths && draft.status === s.status) setDraft(null);
  }, [billing.data, draft]);

  /** One wrapper for every mutation: a busy key for its button, the message, and a reload on success or when what we showed was stale. */
  async function run(key: string, action: () => Promise<string>) {
    if (busy) return;
    setBusy(key);
    setMsg(null);
    try {
      const text = await action();
      setMsg({ kind: "info", text });
      billing.reload();
    } catch (err) {
      setMsg({ kind: "error", text: billingErrorText(err) });
      if (isStale(err)) {
        setDraft(null);
        billing.reload();
      }
    } finally {
      setBusy(null);
    }
  }

  /** PUT replaces the whole account, so the Stripe actions send the form as it stands plus their own field. */
  const upsert = (extra: Pick<UpsertBillingAccountRequest, "stripeCustomerId" | "billingEmail"> = {}) =>
    api.admin.billing.upsertAccount(id, {
      version: account?.version ?? 0,
      plan: form.plan,
      effectiveOn: form.effectiveOn,
      termMonths,
      status: form.status,
      ...extra,
    });

  const saveAccount = (e: React.FormEvent) => {
    e.preventDefault();
    void run("account", async () => {
      await upsert();
      return "Saved the billing account.";
    });
  };
  const linkCustomer = (e: React.FormEvent) => {
    e.preventDefault();
    void run("link", async () => {
      const res = await upsert({ stripeCustomerId: customerId.trim() });
      setCustomerId("");
      return `Linked Stripe customer ${res.customer?.name ?? res.account.stripeCustomerId ?? customerId.trim()}.`;
    });
  };
  const createCustomer = (e: React.FormEvent) => {
    e.preventDefault();
    void run("create", async () => {
      const res = await upsert({ billingEmail: billingEmail.trim() });
      setBillingEmail("");
      return `Created Stripe customer ${res.account.stripeCustomerId ?? ""} for ${billing.data?.organization.name ?? "the organization"}.`;
    });
  };
  const createSetupLink = () =>
    void run("setup", async () => {
      const res = await api.admin.billing.createSetupLink(id);
      setSetupLink(res.url);
      setCopied(false);
      return "Setup link created. Send it to the practice.";
    });
  const startProgramFee = () =>
    void run("start", async () => {
      const res = await api.admin.billing.startProgramFee(id);
      return `Program Fee subscription started for ${res.subscription.quantity} location${res.subscription.quantity === 1 ? "" : "s"}; the first charge is on ${res.subscription.billingCycleAnchor.slice(0, 10)}.`;
    });
  const stopProgramFee = () => {
    setConfirmStop(false);
    void run("stop", async () => {
      const res = await api.admin.billing.stopProgramFee(id);
      return `Program Fee subscription stopped (${res.subscription.status}).`;
    });
  };

  async function copyLink() {
    if (!setupLink) return;
    try {
      await navigator.clipboard.writeText(setupLink);
      setCopied(true);
    } catch {
      setMsg({ kind: "error", text: "Couldn't copy the link; select it and copy it by hand." });
    }
  }

  const hasPaymentMethod = account?.paymentMethodStatus === "saved";
  const subscriptionRunning = Boolean(account?.programFeeSubscriptionId) && account?.programFeeSubscriptionStatus !== "canceled";
  const activeLocations = billing.data?.activeLocations ?? 0;
  const noStartReason = !account
    ? "Save the billing account first."
    : account.plan === "alc"
      ? "The A la Carte plan is charged per executed test, not by subscription."
      : account.plan === "cus"
        ? "A Custom plan is billed by hand; it has no subscription here."
        : !account.stripeCustomerId
          ? "Link or create the Stripe customer first."
          : !hasPaymentMethod
            ? "The practice has no payment method on file yet."
            : activeLocations < 1
              ? "The organization has no active locations yet."
              : subscriptionRunning
                ? "A subscription is already running."
                : null;

  const organizationName = billing.data?.organization.name;
  const planOptions = BILLING_PLANS.map((p) => ({ value: p, label: BILLING_PLAN_LABELS[p] }));
  const setForm = (patch: Partial<Draft>) => setDraft({ ...form, ...patch });

  return (
    <>
      <div className="mb-8">
        <nav aria-label="Breadcrumb" className="mb-4 text-small">
          <Link to="/organizations" className="text-anchor underline">Organizations</Link>
          {organizationName && <span className="text-ink-secondary"> / {organizationName}</span>}
        </nav>
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Billing</h1>
        <p className="max-w-[68ch] text-ink-secondary">The practice's plan and term, its Stripe customer, whether a payment method is on file, and the Program Fee subscription. The contract is signed outside the platform. Every change here is audited.</p>
      </div>
      {billing.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load the billing account (${billingErrorText(billing.error)})`} /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      {billing.state === "ready" && !account && <div className="mb-6"><Status kind="warning" label="This organization has no billing account yet. Choose the plan and term, then link or create its Stripe customer to create the account." /></div>}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card as="section">
          <h2 className="mb-4 text-h3 font-semibold text-anchor">Account</h2>
          <form onSubmit={saveAccount} className="grid gap-4">
            <Select label="Plan" value={form.plan} onChange={(e) => setForm({ plan: e.target.value as BillingPlan })} options={planOptions} disabled={billing.state !== "ready"} />
            <Field label="Effective on" type="date" value={form.effectiveOn} onChange={(e) => setForm({ effectiveOn: e.target.value })} required disabled={billing.state !== "ready"} hint="The contract's start date; Base and Core are charged on its monthly anniversary." />
            <Field
              label="Term (months)"
              type="number"
              inputMode="numeric"
              min={1}
              max={120}
              step={1}
              value={form.termMonths}
              onChange={(e) => setForm({ termMonths: e.target.value })}
              required
              disabled={billing.state !== "ready"}
              error={form.termMonths !== "" && !termValid ? "Between 1 and 120 months." : undefined}
            />
            <Select label="Status" value={form.status} onChange={(e) => setForm({ status: e.target.value as AccountStatus })} options={STATUS_OPTIONS} disabled={billing.state !== "ready"} />
            {account ? (
              <div>
                <Button type="submit" disabled={busy !== null || billing.state !== "ready" || !termValid || !form.effectiveOn || !changed}>
                  {busy === "account" ? "Saving…" : "Save account"}
                </Button>
              </div>
            ) : (
              <p className="text-small text-ink-secondary">A new account is created together with its Stripe customer: link or create one on the right, and these choices are saved with it.</p>
            )}
          </form>
          {account && (
            <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-small text-ink-secondary">
              <dt>Version</dt>
              <dd>{account.version}</dd>
              <dt>Updated</dt>
              <dd>{new Date(account.updatedAt).toLocaleString()}</dd>
            </dl>
          )}
        </Card>

        <Card as="section">
          <h2 className="mb-4 text-h3 font-semibold text-anchor">Stripe customer</h2>
          {customer || account?.stripeCustomerId ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-ink-secondary">Name</dt>
              <dd>{customer?.name ?? <span className="text-ink-secondary">Not on the customer</span>}</dd>
              <dt className="text-ink-secondary">Email</dt>
              <dd>{customer?.email ?? <span className="text-ink-secondary">Not on the customer</span>}</dd>
              <dt className="text-ink-secondary">Customer id</dt>
              <dd className="text-small">{customer?.id ?? account?.stripeCustomerId}</dd>
            </dl>
          ) : (
            <div className="grid gap-6">
              <p className="text-ink-secondary">No Stripe customer is linked. Link the one DocuSign created, or create a new one from the organization name and a billing email.</p>
              <form onSubmit={linkCustomer} className="grid gap-3">
                <Field label="Link existing Stripe customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="cus_…" pattern="cus_.+" hint="The customer id from Stripe, starting with cus_." disabled={billing.state !== "ready"} />
                <div>
                  <Button type="submit" variant="secondary" surface="card" disabled={busy !== null || billing.state !== "ready" || !customerId.trim().startsWith("cus_") || !termValid}>
                    {busy === "link" ? "Linking…" : "Link customer"}
                  </Button>
                </div>
              </form>
              <form onSubmit={createCustomer} className="grid gap-3">
                <Field label="Billing email for a new customer" type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} hint="The practice's billing contact. Stripe sends receipts and the platform sends order notices there." disabled={billing.state !== "ready"} />
                <div>
                  <Button type="submit" variant="secondary" surface="card" disabled={busy !== null || billing.state !== "ready" || !billingEmail.includes("@") || !termValid}>
                    {busy === "create" ? "Creating…" : "Create customer"}
                  </Button>
                </div>
              </form>
              {!account && <p className="text-small text-ink-secondary">Either action creates the billing account with the plan, dates and status chosen on the left.</p>}
            </div>
          )}

          <h3 className="mb-2 mt-8 text-body font-semibold text-anchor">Payment method</h3>
          <p className="mb-3">Payment method on file: <strong>{hasPaymentMethod ? "yes" : "no"}</strong></p>
          <Button variant="secondary" surface="card" onClick={createSetupLink} disabled={busy !== null || !account?.stripeCustomerId}>
            {busy === "setup" ? "Creating…" : "Create setup link"}
          </Button>
          {!account?.stripeCustomerId && <p className="mt-2 text-small text-ink-secondary">Link or create the Stripe customer first.</p>}
          {setupLink && (
            <div className="mt-4 grid gap-2">
              <Field label="Setup link" value={setupLink} readOnly wide onFocus={(e) => e.currentTarget.select()} />
              <p className="text-small text-ink-secondary">Send this link to the practice; it saves one payment method and expires in 24 hours.</p>
              <div>
                <Button variant="secondary" surface="card" onClick={() => void copyLink()}>{copied ? "Copied" : "Copy link"}</Button>
              </div>
            </div>
          )}
        </Card>

        <Card as="section" className="lg:col-span-2">
          <h2 className="mb-4 text-h3 font-semibold text-anchor">Program Fee</h2>
          <Table caption="The monthly subscription for Base and Core: one unit per active location, charged in advance on the contract's monthly anniversary.">
            <thead>
              <tr>
                <Th>Subscription</Th>
                <Th>Status</Th>
                <Th>Active locations</Th>
                <Th><span className="sr-only">Actions</span></Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>{account?.programFeeSubscriptionId ?? <span className="text-ink-secondary">None</span>}</Td>
                <Td>{account?.programFeeSubscriptionStatus ?? <span className="text-ink-secondary">Not started</span>}</Td>
                <Td>{billing.data ? activeLocations : "…"}</Td>
                <Td>
                  {subscriptionRunning ? (
                    confirmStop ? (
                      <div className="grid gap-2">
                        <p className="text-small">Stop the Program Fee subscription now? Stripe cancels it at once and the practice is not charged again.</p>
                        <div className="flex flex-wrap gap-2">
                          <Button onClick={stopProgramFee} disabled={busy !== null}>{busy === "stop" ? "Stopping…" : "Yes, stop it"}</Button>
                          <Button variant="secondary" surface="card" onClick={() => setConfirmStop(false)} disabled={busy !== null}>Keep it</Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="secondary" surface="card" onClick={() => setConfirmStop(true)} disabled={busy !== null}>Stop</Button>
                    )
                  ) : (
                    <div className="grid gap-1">
                      <div>
                        <Button onClick={startProgramFee} disabled={busy !== null || billing.state !== "ready" || noStartReason !== null}>
                          {busy === "start" ? "Starting…" : "Start"}
                        </Button>
                      </div>
                      {noStartReason && billing.state === "ready" && <p className="text-small text-ink-secondary">{noStartReason}</p>}
                    </div>
                  )}
                </Td>
              </tr>
            </tbody>
          </Table>
        </Card>
      </div>
    </>
  );
}
